import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, Gamepad2, MonitorPlay, Send, Trash2, Tv, Upload } from 'lucide-react';

import { ApiError } from '@/lib/api';
import { ghostPillClass, inputClass, pillClass } from '@/features/settings/controls';
import { uploadMedia } from '@/features/settings/upload';
import { useResource } from '@/features/settings/useSettings';
import { useAuth } from '@/features/auth/useAuth';
import {
  SCREENS_PATH,
  addTicks,
  assetUsageCount,
  assignDeviceGame,
  assignIdleMedia,
  chooserGames,
  deleteIdleAsset,
  errorKeyFor,
  formatMegabytes,
  gamePcs,
  helperSelection,
  onlineDevices,
  pushIdleMedia,
  pushToDevices,
  setGameNotice,
  tickedPcIds,
  toggleTick,
  type PushOutcome,
  type ScreenDeviceDto,
  type ScreenGameDto,
  type ScreenMediaAssetDto,
  type ScreensResponse,
} from './screens';

/**
 * Screens (SPEC §7) — which idle video loops on which machine while it waits.
 *
 * Two panes: the venue's idle-video library (upload/delete), and the device
 * grid (assign, push now, push to all online) — plus, since 2026-09-24, each
 * PC's game and "Set game…", because a PC set to a game plays that game's
 * video on its lock screen before its own. The page owns fetching and the
 * handlers; `ScreensBody` below is pure presentation so the states a venue
 * must be able to tell apart — loading, failed-to-load, genuinely empty — are
 * pinned by static-render tests rather than by hope.
 */
export function ScreensPage() {
  const { t } = useTranslation();
  const { can } = useAuth();

  const {
    data,
    loading,
    error: loadError,
    reload,
  } = useResource<ScreensResponse>(can('media.manage') ? SCREENS_PATH : null);

  const [busyDeviceId, setBusyDeviceId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<PushOutcome[] | null>(null);
  /** One inline notice per device — the last assign/push answer for it. */
  const [deviceNotice, setDeviceNotice] = useState<{ deviceId: string; text: string } | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  /** "Set game…" — null while closed; the ticked PCs while open. */
  const [setGame, setSetGame] = useState<SetGameState | null>(null);
  const [setGameNoticeText, setSetGameNoticeText] = useState<string | null>(null);

  if (!can('media.manage')) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('screens.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('screens.noPermission')}</p>
      </div>
    );
  }

  function refusalText(caught: unknown): string {
    if (caught instanceof ApiError)
      return t(errorKeyFor(caught.code), { defaultValue: caught.message });
    return t('screens.errors.generic');
  }

  async function handleAssign(deviceId: string, mediaAssetId: string | null): Promise<void> {
    setBusyDeviceId(deviceId);
    setDeviceNotice(null);
    try {
      await assignIdleMedia(deviceId, mediaAssetId);
      await reload();
    } catch (caught) {
      setDeviceNotice({ deviceId, text: refusalText(caught) });
    } finally {
      setBusyDeviceId(null);
    }
  }

  async function handlePush(device: ScreenDeviceDto): Promise<void> {
    setBusyDeviceId(device.id);
    setDeviceNotice(null);
    try {
      await pushIdleMedia(device.id);
      setDeviceNotice({ deviceId: device.id, text: t('screens.bulk.sent') });
    } catch (caught) {
      setDeviceNotice({ deviceId: device.id, text: refusalText(caught) });
    } finally {
      setBusyDeviceId(null);
    }
  }

  async function handlePushAllOnline(): Promise<void> {
    if (!data) return;
    setBulkBusy(true);
    setOutcomes(null);
    try {
      setOutcomes(await pushToDevices(onlineDevices(data.devices)));
    } finally {
      setBulkBusy(false);
    }
  }

  function updateSetGame(change: (current: SetGameState) => SetGameState): void {
    setSetGame((current) => (current ? change(current) : current));
  }

  function handleToggleTick(deviceId: string): void {
    updateSetGame((current) => ({
      ...current,
      ticked: toggleTick(current.ticked, deviceId),
      error: null,
    }));
  }

  function handleTickMany(deviceIds: readonly string[]): void {
    updateSetGame((current) => ({
      ...current,
      ticked: addTicks(current.ticked, deviceIds),
      error: null,
    }));
  }

  async function handleApplySetGame(game: ScreenGameDto | null): Promise<void> {
    if (!data || !setGame) return;
    const deviceIds = tickedPcIds(setGame.ticked, data.devices);
    if (deviceIds.length === 0) return;
    updateSetGame((current) => ({ ...current, busy: true, error: null }));
    try {
      const { updated } = await assignDeviceGame(deviceIds, game?.id ?? null);
      const notice = setGameNotice(updated, deviceIds.length, game);
      setSetGame(null);
      setSetGameNoticeText(t(notice.key, notice.params));
      await reload();
    } catch (caught) {
      updateSetGame((current) => ({ ...current, busy: false, error: refusalText(caught) }));
    }
  }

  async function handleDeleteAsset(asset: ScreenMediaAssetDto): Promise<void> {
    const used = assetUsageCount(asset) > 0;
    // The references are ON DELETE SET NULL server-side, so deleting an in-use
    // video silently blanks screens — the guard makes staff say so out loud.
    const question = used
      ? t('screens.library.confirmDeleteInUse', {
          name: asset.fileName,
          devices: asset.usedByDeviceCount,
          groups: asset.usedByGroupCount,
        })
      : t('screens.library.confirmDelete', { name: asset.fileName });
    if (!window.confirm(question)) return;

    setLibraryError(null);
    try {
      await deleteIdleAsset(asset.id);
      await reload();
    } catch {
      setLibraryError(t('screens.library.deleteFailed'));
    }
  }

  return (
    <ScreensBody
      loading={loading}
      loadError={loadError}
      response={data}
      busyDeviceId={busyDeviceId}
      bulkBusy={bulkBusy}
      outcomes={outcomes}
      deviceNotice={deviceNotice}
      libraryError={libraryError}
      onAssign={(deviceId, mediaAssetId) => void handleAssign(deviceId, mediaAssetId)}
      onPush={(device) => void handlePush(device)}
      onPushAllOnline={() => void handlePushAllOnline()}
      onDismissOutcomes={() => setOutcomes(null)}
      onDeleteAsset={(asset) => void handleDeleteAsset(asset)}
      onLibraryChanged={() => void reload()}
      onLibraryError={setLibraryError}
      // The request is `devices.manage`: this page is `media.manage`, and an
      // employee trusted with videos need not be trusted with device setup.
      canSetGame={can('devices.manage')}
      setGame={setGame}
      setGameNotice={setGameNoticeText}
      onOpenSetGame={() => {
        setSetGameNoticeText(null);
        setSetGame({ ticked: new Set(), busy: false, error: null });
      }}
      onCloseSetGame={() => setSetGame(null)}
      onToggleTick={handleToggleTick}
      onTickMany={handleTickMany}
      onClearTicks={() => updateSetGame((current) => ({ ...current, ticked: new Set() }))}
      onApplySetGame={(game) => void handleApplySetGame(game)}
      onDismissSetGameNotice={() => setSetGameNoticeText(null)}
    />
  );
}

/** "Set game…" while open: which PCs are ticked, and the last answer. */
export interface SetGameState {
  ticked: ReadonlySet<string>;
  busy: boolean;
  error: string | null;
}

export interface ScreensBodyProps {
  loading: boolean;
  loadError: string | null;
  response: ScreensResponse | null;
  busyDeviceId: string | null;
  bulkBusy: boolean;
  outcomes: PushOutcome[] | null;
  deviceNotice: { deviceId: string; text: string } | null;
  libraryError: string | null;
  onAssign: (deviceId: string, mediaAssetId: string | null) => void;
  onPush: (device: ScreenDeviceDto) => void;
  onPushAllOnline: () => void;
  onDismissOutcomes: () => void;
  onDeleteAsset: (asset: ScreenMediaAssetDto) => void;
  onLibraryChanged: () => void;
  onLibraryError: (message: string | null) => void;
  /** Whether this employee may set PCs' games (`devices.manage`). */
  canSetGame: boolean;
  /** Null while "Set game…" is closed. */
  setGame: SetGameState | null;
  /** The last "Set game…" outcome, shown until dismissed or reopened. */
  setGameNotice: string | null;
  onOpenSetGame: () => void;
  onCloseSetGame: () => void;
  onToggleTick: (deviceId: string) => void;
  onTickMany: (deviceIds: readonly string[]) => void;
  onClearTicks: () => void;
  /** `null` = "No game". */
  onApplySetGame: (game: ScreenGameDto | null) => void;
  onDismissSetGameNotice: () => void;
}

/** Pure presentation. Exported for the static-render tests. */
export function ScreensBody(props: ScreensBodyProps) {
  const { t } = useTranslation();
  const { loading, loadError, response } = props;

  const online = response ? onlineDevices(response.devices) : [];
  const pcs = response ? gamePcs(response.devices) : [];

  return (
    <div className="flex h-full flex-col p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('screens.title')}</h1>
        <div className="flex-1" />
        {props.canSetGame ? (
          <button
            type="button"
            onClick={props.setGame ? props.onCloseSetGame : props.onOpenSetGame}
            disabled={pcs.length === 0}
            aria-pressed={props.setGame !== null}
            className={ghostPillClass}
          >
            <span className="inline-flex items-center gap-1">
              <Gamepad2 className="h-4 w-4" aria-hidden />
              {t('screens.setGame.open')}
            </span>
          </button>
        ) : null}
        <button
          type="button"
          onClick={props.onPushAllOnline}
          disabled={props.bulkBusy || online.length === 0}
          title={online.length === 0 ? t('screens.bulk.noneOnline') : undefined}
          className={pillClass}
        >
          <span className="inline-flex items-center gap-1">
            <Send className="h-4 w-4" aria-hidden />
            {props.bulkBusy
              ? t('screens.bulk.pushing')
              : // `online`, not i18next's plural-triggering `count`.
                t('screens.bulk.pushAllOnline', { online: online.length })}
          </span>
        </button>
      </header>

      {props.outcomes ? (
        <PushResults outcomes={props.outcomes} onDismiss={props.onDismissOutcomes} />
      ) : null}

      {props.setGameNotice ? (
        <div
          role="status"
          className="mb-4 flex items-center gap-3 rounded-card border border-subtle bg-raised p-3 text-sm text-content"
        >
          <span className="min-w-0 flex-1">{props.setGameNotice}</span>
          <button type="button" onClick={props.onDismissSetGameNotice} className={ghostPillClass}>
            {t('screens.bulk.dismiss')}
          </button>
        </div>
      ) : null}

      {props.canSetGame && props.setGame && response ? (
        <SetGamePanel
          games={chooserGames(response.games, response.devices)}
          devices={response.devices}
          state={props.setGame}
          onTickMany={props.onTickMany}
          onClearTicks={props.onClearTicks}
          onApply={props.onApplySetGame}
          onCancel={props.onCloseSetGame}
        />
      ) : null}

      {loading && !response ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : loadError ? (
        // Before the empty states: a failed GET must never read as "no screens".
        <p role="alert" className="text-sm text-danger">
          {loadError}
        </p>
      ) : response ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4 xl:flex-row">
          <MediaLibrary
            media={response.media}
            error={props.libraryError}
            onDelete={props.onDeleteAsset}
            onChanged={props.onLibraryChanged}
            onError={props.onLibraryError}
          />
          <DeviceGrid
            devices={response.devices}
            media={response.media}
            busyDeviceId={props.busyDeviceId}
            notice={props.deviceNotice}
            onAssign={props.onAssign}
            onPush={props.onPush}
            // Ticks appear only while "Set game…" is open, and only on PCs.
            ticked={props.canSetGame && props.setGame ? props.setGame.ticked : null}
            onToggleTick={props.onToggleTick}
          />
        </div>
      ) : null}
    </div>
  );
}

/** The bulk push's per-device answer list — success and refusal, all named. */
function PushResults({ outcomes, onDismiss }: { outcomes: PushOutcome[]; onDismiss: () => void }) {
  const { t } = useTranslation();
  const sent = outcomes.filter((outcome) => outcome.ok).length;

  return (
    <div role="status" className="mb-4 rounded-card border border-subtle bg-raised p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-content">
          {t('screens.bulk.resultsTitle', { sent, total: outcomes.length })}
        </h2>
        <div className="flex-1" />
        <button type="button" onClick={onDismiss} className={ghostPillClass}>
          {t('screens.bulk.dismiss')}
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {outcomes.map((outcome) => (
          <li key={outcome.deviceId} data-push-ok={outcome.ok} className="flex gap-2 text-sm">
            <span className="font-medium text-content">{outcome.deviceName}</span>
            {outcome.ok ? (
              <span className="text-content-secondary">{t('screens.bulk.sent')}</span>
            ) : (
              <span className="text-danger">
                {t(errorKeyFor(outcome.code), { defaultValue: outcome.message ?? '' })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MediaLibrary({
  media,
  error,
  onDelete,
  onChanged,
  onError,
}: {
  media: ScreenMediaAssetDto[];
  error: string | null;
  onDelete: (asset: ScreenMediaAssetDto) => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setUploading(true);
    onError(null);
    try {
      await uploadMedia(file, 'IDLE_VIDEO');
      onChanged();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : t('screens.library.uploadFailed'));
    } finally {
      setUploading(false);
      // Clearing lets the same file be re-picked after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <section className="shrink-0 rounded-card border border-subtle bg-raised p-4 xl:w-80">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-content-secondary">
          {t('screens.library.title')}
        </h2>
        <div className="flex-1" />
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm"
          onChange={(event) => void handleFile(event.target.files?.[0])}
          className="hidden"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className={ghostPillClass}
        >
          <span className="inline-flex items-center gap-1">
            <Upload className="h-4 w-4" aria-hidden />
            {uploading ? t('screens.library.uploading') : t('screens.library.upload')}
          </span>
        </button>
      </div>

      {error ? (
        <p role="alert" className="mb-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {media.length === 0 ? (
        <p className="text-sm text-content-muted">{t('screens.library.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {media.map((asset) => {
            const used = assetUsageCount(asset) > 0;
            return (
              <li
                key={asset.id}
                className="flex items-center gap-3 rounded-control border border-subtle bg-input p-2"
              >
                <Film className="h-5 w-5 shrink-0 text-content-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-content" title={asset.fileName}>
                    {asset.fileName}
                  </p>
                  <p className="text-xs text-content-muted">
                    <span className="tabular">
                      {formatMegabytes(asset.sizeBytes, i18n.language)}
                    </span>
                    {' · '}
                    {used
                      ? t('screens.library.usage', {
                          devices: asset.usedByDeviceCount,
                          groups: asset.usedByGroupCount,
                        })
                      : t('screens.library.unused')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onDelete(asset)}
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function DeviceGrid({
  devices,
  media,
  busyDeviceId,
  notice,
  onAssign,
  onPush,
  ticked,
  onToggleTick,
}: {
  devices: ScreenDeviceDto[];
  media: ScreenMediaAssetDto[];
  busyDeviceId: string | null;
  notice: { deviceId: string; text: string } | null;
  onAssign: (deviceId: string, mediaAssetId: string | null) => void;
  onPush: (device: ScreenDeviceDto) => void;
  /** The PCs ticked for "Set game…", or null while it is closed. */
  ticked: ReadonlySet<string> | null;
  onToggleTick: (deviceId: string) => void;
}) {
  const { t } = useTranslation();

  if (devices.length === 0) {
    return <p className="text-sm text-content-muted">{t('screens.devices.empty')}</p>;
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
        {devices.map((device) => {
          const busy = busyDeviceId === device.id;
          // What the machine would actually play: its own video, else the
          // group's. Spelled out so "assigned nothing" and "inherits from the
          // Wall group" never look the same.
          const inherited = !device.idleMediaAssetId && device.groupIdleMedia;

          return (
            <article key={device.id} className="rounded-card border border-subtle bg-raised p-4">
              <header className="flex items-center gap-2">
                {ticked && device.deviceType === 'PC' ? (
                  <input
                    type="checkbox"
                    checked={ticked.has(device.id)}
                    onChange={() => onToggleTick(device.id)}
                    aria-label={t('screens.setGame.tick', { device: device.name })}
                    data-tick={device.id}
                    className="h-4 w-4 accent-[var(--brand-primary)]"
                  />
                ) : null}
                {/* §7 — "PS-1 basically means TV-1": a TV-driven seat gets the
                    TV mark, so staff read the tile as the wall it changes. */}
                {device.drivesTv ? (
                  <Tv className="h-4 w-4 text-content-muted" aria-hidden />
                ) : (
                  <MonitorPlay className="h-4 w-4 text-content-muted" aria-hidden />
                )}
                <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-content">
                  {device.name}
                </h3>
                {/* No uppercase/letter-spacing: the badge is translated, and
                    tracking visibly breaks Arabic/Kurdish letter joining. */}
                {device.drivesTv ? (
                  <span className="rounded-full border border-subtle px-2 py-0.5 text-[10px] font-semibold text-content-secondary">
                    {t('screens.devices.tvBadge')}
                  </span>
                ) : null}
                {/* The PC's game (2026-09-24) — its video is what the lock
                    screen plays, so the game belongs on the tile. */}
                {device.gameTitle ? (
                  <span
                    data-game-badge={device.gameId ?? ''}
                    title={t('screens.devices.gameBadge', { game: device.gameTitle })}
                    className="inline-flex max-w-[45%] items-center gap-1 rounded-full border border-subtle px-2 py-0.5 text-[10px] font-semibold text-content-secondary"
                  >
                    <Gamepad2 className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="truncate">{device.gameTitle}</span>
                  </span>
                ) : null}
                <span
                  data-online={device.online}
                  className="inline-flex items-center gap-1.5 text-xs text-content-secondary"
                >
                  <span
                    aria-hidden
                    className={`h-2.5 w-2.5 rounded-full ${device.online ? 'bg-available' : 'bg-offline'}`}
                  />
                  {device.online ? t('screens.devices.online') : t('screens.devices.offline')}
                </span>
              </header>

              {/* The bridge being up says nothing about the television. A
                  seat whose TV is off, unplugged or at a stale address used
                  to look exactly like a healthy one — the 2026-08-23 hunt. */}
              {device.drivesTv && device.tvHealth && !device.tvHealth.ok ? (
                <p className="mt-2 rounded-control bg-danger/10 px-2 py-1 text-xs text-danger">
                  {/* "Unauthorised" is not "unreachable", and the difference
                      is the whole fix: developer mode being ON is not the
                      same as this PC having been allowed. The venue lost an
                      evening to exactly that distinction (2026-08-23). */}
                  {/unauthori[sz]ed|not authori[sz]ed/i.test(device.tvHealth.error ?? '')
                    ? t('screens.devices.tvUnauthorised')
                    : t('screens.devices.tvUnreachable')}
                  {device.tvHealth.error ? (
                    <span className="block text-content-muted" dir="ltr">
                      {device.tvHealth.error}
                    </span>
                  ) : null}
                </p>
              ) : null}

              {/* What the lock screen actually plays: the game's video
                  outranks the PC's own, which is then only the fallback
                  (`lockScreenIdleVideoUrl`). A game without a video says
                  so, or the owner would wonder why nothing changed — and
                  says what plays instead: the PC's own video, or with none
                  the kiosk hero. "Plays its own" above "No idle video" told
                  staff two different things (review, 2026-09-24). */}
              {device.playsGameVideo && device.gameTitle ? (
                <p data-plays="game" className="mt-2 text-xs text-content-secondary">
                  {t('screens.devices.gameVideo', { game: device.gameTitle })}
                </p>
              ) : device.gameTitle && device.idleMediaAssetId ? (
                <p data-plays="own" className="mt-2 text-xs text-content-muted">
                  {t('screens.devices.gameNoVideo', { game: device.gameTitle })}
                </p>
              ) : device.gameTitle ? (
                <p data-plays="hero" className="mt-2 text-xs text-content-muted">
                  {t('screens.devices.gameNoVideoHero', { game: device.gameTitle })}
                </p>
              ) : null}

              <p className="mt-2 text-xs text-content-muted">
                {device.idleMediaAssetId && device.playsGameVideo ? (
                  <span>
                    {t('screens.devices.ownFallback', {
                      file: device.idleMediaFileName ?? device.idleMediaAssetId,
                    })}
                  </span>
                ) : device.idleMediaAssetId ? (
                  <span className="text-content-secondary">
                    {device.idleMediaFileName ?? device.idleMediaAssetId}
                  </span>
                ) : inherited ? (
                  <span>
                    {t('screens.devices.fromGroup', {
                      group: device.groupIdleMedia!.groupName,
                      file:
                        device.groupIdleMedia!.mediaFileName ?? device.groupIdleMedia!.mediaAssetId,
                    })}
                  </span>
                ) : (
                  <span>{t('screens.devices.none')}</span>
                )}
              </p>

              <div className="mt-3 flex items-center gap-2">
                <label className="min-w-0 flex-1">
                  <span className="sr-only">
                    {t('screens.devices.assign', { device: device.name })}
                  </span>
                  <select
                    value={device.idleMediaAssetId ?? ''}
                    disabled={busy}
                    onChange={(event) => onAssign(device.id, event.target.value || null)}
                    className={inputClass}
                  >
                    <option value="">{t('screens.devices.noneOption')}</option>
                    {media.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.fileName}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onPush(device)}
                  className={ghostPillClass}
                >
                  {busy ? t('screens.devices.pushing') : t('screens.devices.push')}
                </button>
              </div>

              {notice && notice.deviceId === device.id ? (
                <p role="status" className="mt-2 text-xs text-content-secondary">
                  {notice.text}
                </p>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}

/** The chooser's value for "No game" — `''` is the unchosen placeholder. */
const NO_GAME = '__none__';

/**
 * "Set game…" (owner, 2026-09-24): "for each game i can set a video that will
 * play on all the other PC's that are the same game".
 *
 * Tick PCs on the grid, choose the game (or "No game"), apply. Every PC set to
 * a game plays that game's ONE video — its trailer from Settings > Games — on
 * its lock screen from its next poll. A game with no video is marked in the
 * chooser and explained once chosen, because the PCs set to it play their own
 * video, or the kiosk hero with none, and the owner would otherwise see
 * "nothing happened".
 *
 * The helpers tick the PCs that already look set up for the chosen game, and
 * appear only when there is data behind them: per-PC paths marked installed
 * (staff-entered today — no agent scan writes them yet), and venue accounts
 * made for a PC ("Gamers escape 10 (BO6)").
 *
 * Exported for the static-render tests; `initialChoice` exists for them too.
 */
export function SetGamePanel({
  games,
  devices,
  state,
  onTickMany,
  onClearTicks,
  onApply,
  onCancel,
  initialChoice = '',
}: {
  games: ScreenGameDto[];
  devices: ScreenDeviceDto[];
  state: SetGameState;
  onTickMany: (deviceIds: readonly string[]) => void;
  onClearTicks: () => void;
  onApply: (game: ScreenGameDto | null) => void;
  onCancel: () => void;
  initialChoice?: string;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(initialChoice);

  const chosen = games.find((game) => game.id === choice) ?? null;
  const ticked = tickedPcIds(state.ticked, devices).length;
  const installed = chosen ? helperSelection(chosen.installedDeviceIds, devices) : [];
  const accounts = chosen ? helperSelection(chosen.accountDeviceIds, devices) : [];
  const ready = (choice === NO_GAME || chosen !== null) && ticked > 0 && !state.busy;

  return (
    <section
      aria-label={t('screens.setGame.title')}
      className="mb-4 rounded-card border border-subtle bg-raised p-4"
    >
      <h2 className="text-sm font-semibold text-content">{t('screens.setGame.title')}</h2>
      <p className="mt-1 text-xs text-content-muted">{t('screens.setGame.intro')}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="min-w-56 flex-1">
          <span className="sr-only">{t('screens.setGame.gameLabel')}</span>
          <select
            value={choice}
            disabled={state.busy}
            onChange={(event) => setChoice(event.target.value)}
            className={inputClass}
          >
            <option value="" disabled>
              {t('screens.setGame.choose')}
            </option>
            <option value={NO_GAME}>{t('screens.setGame.noGame')}</option>
            {games.map((game) => (
              <option key={game.id} value={game.id} data-has-video={game.hasVideo}>
                {!game.hasVideo
                  ? t('screens.setGame.optionNoVideo', { game: game.title })
                  : game.isActive
                    ? game.title
                    : t('screens.setGame.hidden', { game: game.title })}
              </option>
            ))}
          </select>
        </label>
        {installed.length > 0 && chosen ? (
          <button
            type="button"
            disabled={state.busy}
            onClick={() => onTickMany(installed)}
            className={ghostPillClass}
          >
            {t('screens.setGame.selectInstalled', { game: chosen.title, pcs: installed.length })}
          </button>
        ) : null}
        {accounts.length > 0 && chosen ? (
          <button
            type="button"
            disabled={state.busy}
            onClick={() => onTickMany(accounts)}
            className={ghostPillClass}
          >
            {t('screens.setGame.selectAccounts', { game: chosen.title, pcs: accounts.length })}
          </button>
        ) : null}
      </div>

      {chosen && !chosen.hasVideo ? (
        <p data-no-video className="mt-2 text-xs text-ending">
          {t('screens.setGame.noVideo', { game: chosen.title })}
        </p>
      ) : null}

      {state.error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {state.error}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-content-secondary tabular">
          {t('screens.setGame.ticked', { pcs: ticked })}
        </span>
        {ticked > 0 ? (
          <button
            type="button"
            disabled={state.busy}
            onClick={onClearTicks}
            className={ghostPillClass}
          >
            {t('screens.setGame.clear')}
          </button>
        ) : null}
        <div className="flex-1" />
        <button type="button" disabled={state.busy} onClick={onCancel} className={ghostPillClass}>
          {t('screens.setGame.cancel')}
        </button>
        <button
          type="button"
          disabled={!ready}
          onClick={() => onApply(choice === NO_GAME ? null : chosen)}
          className={pillClass}
        >
          {/* `count`, not a plain number: "Set 1 PC", and Arabic's own forms. */}
          {state.busy
            ? t('screens.setGame.applying')
            : t('screens.setGame.apply', { count: ticked })}
        </button>
      </div>
    </section>
  );
}
